import React, { useState, FormEvent, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "../components/Button";
import Layout from "../components/layouts/Layout";
import PasswordField from "../components/PasswordField";
import TextField from "../components/TextField";
import ROUTES from "../setup/routes";
import { useAuth } from "../hooks/useAuth";
import { StateContext } from "../store/context";
import { UPDATE_USER } from "../store/types";
import { DispatchContext } from "../store/context";
import ErrorBoundary from "../components/ErrorBoundary";
import AuthScreenLayout from "../components/authScreens/authScreenLayout";
import { terminateUserSession } from "../services/session.service";
import { Button as AntButton } from "antd";
import { ArrowRight } from "lucide-react";
import "../styles/theme-tokens.css";
import "./login.theme.css";

export const _h = "18";

const Login: React.FC = (): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const { authUser = null } = useContext(StateContext);
  const dispatch = useContext(DispatchContext);
  const { execute } = useAuth();

  // No toggle needed here: a user always reaches /login by clicking
  // "Login" from the pre-login preview shell (Dashboard.tsx's guest
  // view), whose own Sidebar already carries the real dark-mode switch
  // and has already set the "dark" class on <html> before this page
  // ever mounts. theme-tokens.css picks that up automatically — this
  // page just needs to render off the same --vi-* tokens, not manage
  // the mode itself.

  const terminateSession = localStorage.getItem("terminate-session");
  console.log("terminateSession: ", terminateSession);

  const goToSignup = (e: any) => {
    e.preventDefault();
    navigate(ROUTES.SIGNUP);
  };

  const goToForgotPassword = (e: any) => {
    e.preventDefault();
    navigate(ROUTES.FORGOT_PASSWORD);
  };

  const login = async (e: any) => {
    e.preventDefault();
    console.log("remember me", rememberMe);
    if (rememberMe) {
      localStorage.setItem("rememberMe", JSON.stringify({ username }));
    }

    if (await execute(username, password)) {
      const is_first_login = localStorage.getItem("is_first_login");
      const is_manually_created = localStorage.getItem("is_manually_created");

      if (is_first_login === "1" && is_manually_created === "1") {
        navigate(`${ROUTES.MANUALLY_CREATED_USER_UPDATE}`);
      } else {
        navigate(`${ROUTES.DASHBOARD}/${ROUTES.OVERVIEW}`);
      }
    }
  };

  const handleProceedWithoutLogin = () => {
    navigate(`${ROUTES.DASHBOARD}/${ROUTES.OVERVIEW}`);
  };

  const handleChange = (e: FormEvent<HTMLInputElement>): void => {
    const id = e.currentTarget.id;
    const value = e.currentTarget.value;

    id === "username" ? setUsername(value) : setPassword(value);
  };

  const handleSessionTerminate = async (e: any) => {
    e.preventDefault();
    const isTerminated: boolean = await terminateUserSession(username);
    if (isTerminated) {
      localStorage.setItem("terminate-session", "false");
      dispatch({ type: UPDATE_USER, payload: null });
      navigate(ROUTES.LOGIN);
    }
  };

  useEffect(() => {
    console.log("authUser", authUser);
    if (authUser && authUser.hasOwnProperty("token")) {
      console.log("authUser", authUser);
      navigate(`${ROUTES.DASHBOARD}/${ROUTES.OVERVIEW}`);
    }
    if (localStorage.getItem("rememberMe")) {
      const rememberMe = JSON.parse(localStorage.getItem("rememberMe") || "{}");
      setRememberMe(true);
      setUsername(rememberMe.username);
      // setPassword(rememberMe.password);
    }
  }, []);

  return (
    <Layout preLogin>
      <div className="vi-login w-full">
        <AuthScreenLayout>
          <div className="vi-login-pane w-full md:w-10/12 p-8 flex flex-col justify-center items-center relative">
            {/* Decorative accent glows — the kind of flourish that earns
                its place on a single, focused auth screen but would be
                noise on a dense data page, so it stays local to this
                page's own stylesheet instead of theme-tokens.css. */}
            <div className="vi-login-glow vi-login-glow-1" />
            <div className="vi-login-glow vi-login-glow-2" />

            <div className="vi-login-card w-full max-w-md relative">
              <div className="vi-login-badge">
                <span className="vi-login-badge-dot" />
                Welcome back
              </div>

              <h1 className="vi-login-title text-[32px] mb-3 leading-tight">
                Login to your account
              </h1>
              <p className="vi-login-subtitle mb-8">
                Enter your credentials to access your portfolio dashboard.
              </p>

              <ErrorBoundary />
              {false && (
                <a
                  href="#"
                  onClick={handleSessionTerminate}
                  className="font-semibold text-red-500 hover:text-red-700 font-dm underline"
                >
                  Terminate other sessions
                </a>
              )}

              <form onSubmit={login} className="space-y-5">
                <div>
                  <label htmlFor="username" className="vi-login-label">
                    Email<span className="vi-login-required">*</span>
                  </label>
                  <div className="mt-2">
                    <TextField
                      full
                      id="username"
                      autoComplete="username"
                      required
                      value={username}
                      placeholder="Enter your registered email"
                      onChange={handleChange}
                      className="vi-login-input rounded-[10px] font-dm h-[44px]"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="vi-login-label">
                    Password<span className="vi-login-required">*</span>
                  </label>
                  <div className="mt-2">
                    <PasswordField
                      full
                      id="password"
                      required
                      value={password}
                      onChange={handleChange}
                      className="vi-login-input rounded-[10px] font-dm h-[44px]"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      checked={rememberMe}
                      className="h-4 w-4 rounded vi-login-checkbox"
                      style={{ accentColor: "var(--vi-accent)" }}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <label
                      htmlFor="remember-me"
                      className="ml-3 block text-sm leading-6 vi-login-muted font-dm"
                    >
                      Remember me
                    </label>
                  </div>

                  <div className="text-sm leading-6">
                    <a
                      href="#"
                      onClick={goToForgotPassword}
                      className="vi-login-link font-semibold font-dm"
                    >
                      Forgot password?
                    </a>
                  </div>
                </div>

                <div className="w-full flex flex-col gap-3">
                  <AntButton
                    type="primary"
                    htmlType="submit"
                    size="large"
                    disabled={!username || !password}
                    className="vi-login-submit w-full font-dm text-base font-medium"
                    style={{
                      background: "var(--vi-accent-grad)",
                      borderRadius: "10px",
                      color: "var(--vi-accent-on)",
                      border: "none",
                    }}
                    styles={{
                      icon: {
                        color: "var(--vi-accent-on)",
                      },
                    }}
                  >
                    Login
                  </AntButton>
                </div>
                <hr className="vi-login-divider" />
                <div>
                  <p className="mt-2 text-sm leading-6 vi-login-muted font-dm text-center">
                    Don't have an account?{" "}
                    <a
                      href="#"
                      onClick={goToSignup}
                      className="vi-login-cta group font-bold"
                    >
                      Create one free
                      <ArrowRight
                        size={15}
                        strokeWidth={2.5}
                        className="vi-login-cta-arrow"
                      />
                    </a>
                  </p>
                </div>
              </form>

              <p className="text-center text-sm vi-login-muted mt-8">
                ©2026 Valueit. All rights reserved.
              </p>
            </div>
          </div>
        </AuthScreenLayout>
      </div>
    </Layout>
  );
};

export default Login;
